import env from './env'
import VueI18n from 'vue-i18n'
import Vue from 'vue'
import lodash from 'lodash'
import { Message } from 'element-ui'
import ElementLang_EN from 'element-ui/lib/locale/lang/en'
import ElementLang_ZH from 'element-ui/lib/locale/lang/zh-CN'
import LocalLang_EN from '@/locales/en'
import LocalLang_ZH from '@/locales/zh'

Vue.use(VueI18n)

/** 是否允许页面切换语言 **/
const allowDisplay = env.check('VUE_APP_LOCALES_ALLOWDISPLAY') ? env.getEnv('VUE_APP_LOCALES_ALLOWDISPLAY', 'Boolean') : true
/** 是否跟随浏览器 **/
const followSystem = env.check('VUE_APP_LOCALES_FOLLOWSYSTEM') ? env.getEnv('VUE_APP_LOCALES_FOLLOWSYSTEM', 'Boolean') : true // 在用户没有切换语言的情况下生效
/** 是否允许在当前语言和将要切换语言相同时切换（当相与刷新页面） **/
const allowSameSwitch = env.check('VUE_APP_LOCALES_FOLLOWSYSTEM') ? env.getEnv('VUE_APP_LOCALES_FOLLOWSYSTEM', 'Boolean') : false
/** 默认语言 **/
const defaultLang = env.check('VUE_APP_LOCALES_DEFAULTLANG') ? env.getEnv('VUE_APP_LOCALES_DEFAULTLANG') : 'zh'
/** 获取浏览器语言标识 **/
const browserLang = (navigator.language || navigator.userLanguage).toLowerCase()
/** 缓存中的语言表示 **/
const savedLang = localStorage.getItem('locale') // 可以改成获取用户账号缓存
/** 主语言代码 **/
const mainLang = savedLang || browserLang.substring(0, browserLang.indexOf('-')) // 将 zh-CN、zh-TW、zh-HK转变为 zh 统一使用简体中文, 有需要再拆分，台湾和香港统一使用繁体，但是文本语序与普通话不一样
/** 匹配支持的语言列表 **/
const supportedLangs = ['en', 'zh']
const supportedNames = ['英语', '中文']
/** 当前系统语言 **/
const systemLang = supportedLangs.includes(mainLang) ? mainLang : defaultLang
/** 当前语言 **/
const currentLocale = followSystem ? systemLang : savedLang || defaultLang
/** 广播器 **/
const channel = new BroadcastChannel('locales') // 使打开的其他页面同步进行中英文切换，因为它切换语言后有缓存，又因为每次切换语言都要读取一遍缓存，会和 allowSameSwitch 开关发生冲突啦。也可以作为多组件时不引用判断切换语言的事件了\
/** 浏览器标签标识 **/
const pageId = new Date().getTime()
/** 多语言语言包 */
const messages = { en: lodash.merge(ElementLang_EN, LocalLang_EN), zh: lodash.merge(ElementLang_ZH, LocalLang_ZH) }
/** 多语言官方组件 */
const locales = new VueI18n({
  locale: currentLocale, // 设置语言
  fallbackLocale: defaultLang, // 回退语言,
  // 自定义数字格式
  numberFormats: {
    en: {
      currency: {
        style: 'currency',
        currency: 'USD'
      },
      currency2: {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2
      },
      toFixed2: {
        style: 'decimal',
        minimumFractionDigits: 2
      }
    },
    zh: {
      currency: {
        style: 'currency',
        currency: 'CNY'
      },
      currency2: {
        style: 'currency',
        currency: 'CNY',
        minimumFractionDigits: 2
      },
      toFixed2: {
        style: 'decimal',
        minimumFractionDigits: 2
      }
    }
  },
  messages: messages
})

initLanguage()

/** 初始化语言环境 **/
function initLanguage() {
  const supportedEnum = {}
  supportedLangs.forEach((lang) => {
    supportedEnum[lang.replace(/-(\w)/g, (_, char) => char.toUpperCase())] = lang
  })
  /// 向vue中注入语言枚举
  Vue.prototype.lang = supportedEnum
  Object.defineProperty(Vue.prototype, "$locale", {
    get(){
      return locales.locale
    }
  })
  /// 改变全局样式
  releaseCss(currentLocale)
  // 监听语言切换
  channel.addEventListener('message', (e) => {
    /// 因为切换语言时会刷新页面，所以阻止发出方进行广播
    if (pageId == e.data.pageId) {
      return
    }
    change(e.data.lang, false)
  })
}


/** 改变语言 **/
export function change(lang, broadcast) {
  const oldLang = getLanguage()

  if (!supportedLangs.includes(lang)) {
    Message['error']({
      message: locales.t(`locales.error`, { targetLanguage: lang, secLanguage: 3 })
    })
    setTimeout(() => {
      change(defaultLang)
    }, 2400)
    return
  }
  if (!allowSameSwitch && getLanguage() == lang) {
    return
  }

  localStorage.setItem('locale', lang)
  locales.locale = lang

  releaseCss(lang, oldLang)

  /// 判断是否需要广播
  if (broadcast) {
    channel.postMessage({
      lang,
      pageId
    })
  }

  /// 刷新页面，因为组件绑定的数据没有办法直接切换
  setTimeout(() => {
    location.reload()
  }, 600)
}

/** 获取当前语言 */
export function getLanguage() {
  return localStorage.getItem('locale') || locales.locale
}

/**
 * vue 语言指令
 * v-locales="lang.zh"	仅在中文时可见
 * v-locales="lang.en"	仅在英文时可见
 * v-locales.zh/v-locales.en="true"	仅在中文时可见
 * v-locales.en/v-locales.en="true"	仅在英文时可见
 * v-locales.zh="false"	在中文时不可见
 * v-locales.en="false"	在英文时不可见
 */
Vue.directive('locales', {
  inserted: function (el, binding) {
    const config = {}
    const modifiers = Object.keys(binding.modifiers)
    if (modifiers && modifiers.length) {
      config['locale'] = modifiers[0]
      if (binding.value == undefined || binding.value == null) {
        config['show'] = true
      } else {
        config['show'] = binding.value
      }
    } else {
      config['locale'] = binding.value
      config['show'] = true
    }
    console.log(config)
    if (!config['locale']) {
      return
    }
    if (!supportedLangs.includes(config['locale'])) {
      throw new Error(locales.t('locales.error', { targetLanguage: config['locale'] }))
    }
    if ((config.locale == getLanguage()) == config.show) {
      if (el._originalDisplay) {
        el.style.display = el._originalDisplay
      }
    } else {
      el._originalDisplay = el.style.display
      el.style.display = 'none' // 更推荐隐藏而非直接移除
    }
  },
  unbind: function (el) {}
})

/**
 * 改变全局样式
 * .locale-en::v-deep {  // 自己页面的class  }	定义自己页面在英文模式下的样式
 * .locale-zh::v-deep {  // 自己页面的class  }	定义自己页面在中文模式下的样式
 */
function releaseCss(lang, oldLang) {
  if (oldLang) {
    document.body.classList.remove(`locale-${oldLang}`)
  }
  document.body.classList.add(`locale-${lang}`)
  document.documentElement.lang = lang
  document.title = locales.t(`locales.title`)
}

/** 语言切换下拉组件 */
Vue.component('locales-select', {
  props: {
    // 是否跟随展示语言
    followText: {
      type: Boolean,
      default: false
    }
  },
  render: function (h) {
    const { followText } = this

    let langText = locales.t('locales.lang')
    if (followText) {
      langText = locales.t(`locales.${getLanguage()}`)
    }
    if (!allowDisplay) {
      return h(null, {}, '不允许切换多语言')
    }
    return h('div', {}, [
      h('el-dropdown', { props: { trigger: 'click' } }, [
        h('span', { class: 'el-dropdown-link' }, [langText, h('i', { class: 'el-icon-arrow-down el-icon--right' })]),
        h(
          'el-dropdown-menu',
          { class: 'dropdown-locales', slot: 'dropdown' },
          supportedLangs.map((lang) => {
            return h('el-dropdown-item', {}, [
              h('a', { style: 'display: inline-block;width: 100%;height: 100%;', href: 'javascript:void(0)', on: { click: () => change(lang, true) } }, locales.t(`locales.${lang}`))
            ])
          })
        )
      ]),
      this.$slots.after
    ])
  }
})


export default locales
